import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Smartphone, ArrowRight } from 'lucide-react';

const AddInstanceModal = ({ open, onClose, onSubmit }) => {
  const [instanceName, setInstanceName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (instanceName.trim()) {
      setIsLoading(true);
      onSubmit(instanceName.trim());
      setInstanceName('');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setInstanceName('');
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl ring-1 ring-gray-100 p-4"
          initial={{ scale: 0.96, opacity: 0, y: 14 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 14 }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="rounded-full p-2 mr-2.5 ring-1 ring-gray-200 bg-[#01CC54]/10">
                <Smartphone size={18} className="text-[#01CC54]" />
              </div>
              <h2 className="text-lg font-extrabold text-gray-900">
                Create New Instance
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40 transition"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Instance Name
              </label>
              <input
                type="text"
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                placeholder="Enter instance name (e.g., My Business Bot)"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 outline-none transition text-sm"
                maxLength={50}
                required
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Name must be between 1–50 characters
              </p>
            </div>

            {/* Info Box */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-4">
              <h4 className="font-semibold text-gray-900 mb-1.5 text-sm">What's Next?</h4>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>• Choose a subscription plan for your instance</p>
                <p>• Complete secure payment via Razorpay</p>
                <p>• Your instance will be created automatically</p>
                <p>• Start using WhatsApp Business API immediately</p>
              </div>
            </div>

            {/* Pricing Preview */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Starting from</p>
                  <p className="text-xl font-extrabold text-gray-900">₹1,999</p>
                  <p className="text-xs text-gray-500">per month</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-600">Best Value</p>
                  <p className="text-base font-bold text-[#01CC54]">₹10,999</p>
                  <p className="text-xs text-gray-500">6 months (Save 45%)</p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-[#01CC54]/30 text-sm font-medium"
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={!instanceName.trim() || isLoading}
                className="flex-1 px-3 py-2 rounded-lg bg-[#01CC54] hover:bg-[#01CC54]/90 text-white font-semibold shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40 text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/60 border-t-transparent" />
                ) : (
                  <>
                    Payment
                    <ArrowRight size={16} className="ml-1.5" />
                  </>
                )}
              </motion.button>
            </div>
          </form>

          {/* Footer Note */}
          <p className="text-center text-xs text-gray-500 mt-3">
            Each WhatsApp instance requires a separate subscription
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddInstanceModal;
